import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Map from "@/components/Map";
import NewsletterPopup from "@/components/NewsletterPopup";
import Lightbox from "@/components/Lightbox";
import UrgencyBanner from "@/components/UrgencyBanner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const IMG = {
  // Real Ithaca Garden Villa photography, provided by the owner (2026-02).
  // Full commercial rights — no attribution required.
  //
  //  hero     — evening bistro table by the stone wall, amphora, olive tree, warm lamp
  //  dining   — pergola dining table at dusk, two lamps glowing, vine canopy overhead
  //  interior — the guest bedroom: cream headboard, framed abstracts, brass lamps
  //  view     — pergola dining glimpsed through leaves, moody + cinematic
  hero: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/rcjbx8ay_IMG_0036.jpg",
  dining: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/8j62n7aj_IMG_0028.jpg",
  interior: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/t1cq9tlt_IMG_9704.jpg",
  view: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/njztj5vf_IMG_0032.jpg",
};

// Secondary gallery tiles — Rows 2, 3 and beyond. Extend this array to add
// more photos. Flags:
//   • `wide: true` → banner-style row (full width, spans 3 rows tall)
//   • `tall: true` → portrait tile (spans 4 rows tall, same width as landscape)
// Everything else renders as a landscape tile, 3-per-row on desktop and
// 2-per-row on mobile. `grid-auto-flow: dense` packs efficiently.
const GALLERY_EXTRA = [
  // ---- Exterior arrival ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/u7nkk788_IMG_0007.jpg",
    alt: "Sunlit outdoor shower area with amphora and rolled towels",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/2veli0en_IMG_0023.jpg",
    alt: "Entrance to the villa from a quiet Kioni lane",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/yfwps7ck_IMG_9995.jpg",
    alt: "Pergola dining table in the midday sun — grapes, jug of chilled wine, hibiscus blossoms overhead",
  },
  // ---- Interior reveal (WIDE BANNER) ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/as8bglon_IMG_9973.jpg",
    alt: "Open-plan interior — dining table, kitchen beyond, living room and doors out to the courtyard",
    wide: true,
  },
  // ---- Common rooms ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/vk9xhpjm_IMG_9907.jpg",
    alt: "Sitting corner — light-wood writing desk with lamp, botanical print on exposed stone, single sofa and woven chair",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/p3d542c0_IMG_9949.jpg",
    alt: "Kitchen — white cabinetry against an exposed-stone accent wall, shuttered window, amphoras on the counter",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/5t1n43ew_IMG_9849.jpg",
    alt: "Dining table — glass top, framed botanicals, olive branches, kitchen just beyond",
  },
  // ---- Primary bedroom ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/9z5bujg5_IMG_9717.jpg",
    alt: "Primary bedroom with garden view through the window",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/xfq4s4rm_IMG_9724.jpg",
    alt: "Primary bedroom with cream wardrobe and brass reading lamp",
  },
  // ---- Second bedroom — twin beds ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/gs4y0ily_IMG_9746.jpg",
    alt: "Second bedroom — twin beds with green floral headboards, exposed stone walls, framed Renaissance portraits",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/fu9cwfw3_IMG_9768.jpg",
    alt: "Second bedroom wide view — twin beds, garden windows, hand-planed wood ceiling",
  },
  // ---- Bathrooms ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/mgu1tt0k_IMG_9800.jpg",
    alt: "Guest bathroom — rain shower behind glass, vertical mosaic accent",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/s3n1p94r_IMG_9810.jpg",
    alt: "Family bathroom — walk-in shower, amphora, olive tree in stoneware, stone vanity",
  },
  // ---- Living room close-up (WIDE BANNER) ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/1qz1a8dc_IMG_9835.jpg",
    alt: "Living room close-up — beige sofas, framed botanicals on exposed stone, olive branches on the coffee table",
    wide: true,
  },
  // ---- Portrait detail shots ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/g818zy29_IMG_9738.jpg",
    alt: "Detail: yellow amphora resting at the foot of a linen-clad bed",
    tall: true,
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/qyw3gtyx_IMG_9786.jpg",
    alt: "Second bedroom detail — Michelangelo's Delphic Sibyl above the twin bed, olive stems in the window vase",
    tall: true,
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/ha98kk4t_IMG_9791.jpg",
    alt: "Second bedroom detail — brass gooseneck lamp against the exposed stone wall",
    tall: true,
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/zwfbnkxb_IMG_9983.jpg",
    alt: "Bistro table detail — cool lime water, sliced limes on a wooden board, rosemary sprigs in a fluted glass vase",
    tall: true,
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/c1jpx4vq_IMG_9923.jpg",
    alt: "Dining still-life — glass table with wooden chairs, grapes on a footed dish, ceramic bowls, yellow amphora against the textured stone wall",
    tall: true,
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/fr2ebna5_IMG_9999.jpg",
    alt: "Pergola dining glimpsed through the vine leaves in the midday sun — striped cushions, glass table below",
    tall: true,
  },
  // ---- Closing banner ----
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_greek-villa-getaway/artifacts/lmdsf0x7_IMG_0022.jpg",
    alt: "Approach to the villa through the olive-covered hills of Kioni",
    wide: true,
  },
];

// Films — video content that plays below the location section. Append new
// entries to grow. Each film has `src`, optional `poster`, `caption`, and
// autoplays muted-looping when it scrolls into view (see In Motion section).
const FILMS = [
  {
    src: "/films/kioni-harbour.mp4",
    poster: "/films/kioni-harbour-poster.jpg",
    caption: "Kioni harbour from above — the villa's front garden of sky and sea",
  },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.9 } } };

/**
 * FilmTile — autoplays muted-looping when the tile scrolls into view via
 * IntersectionObserver, pauses when scrolled away (saves mobile bandwidth +
 * respects reduced-motion preferences implicitly via `muted` + `playsInline`).
 * User can still press the native `controls` overlay to pause/scrub/fullscreen.
 */
function FilmTile({ film, index, solo = false }) {
  const videoRef = useRef(null);
  const inViewRef = useRef(false);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;

    const tryPlay = () => {
      if (!inViewRef.current) return;
      const p = vid.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => { /* autoplay blocked — user can press play */ });
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          // Kick preload up now that we're visible, then try playing.
          if (vid.preload !== "auto") vid.preload = "auto";
          tryPlay();
        } else {
          vid.pause();
        }
      },
      // Fire slightly earlier so buffering starts before tile is fully visible.
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(vid);

    // If buffering wasn't ready when we first tried, play as soon as it is.
    const onCanPlay = () => tryPlay();
    vid.addEventListener("canplay", onCanPlay);
    vid.addEventListener("loadeddata", onCanPlay);

    return () => {
      io.disconnect();
      vid.removeEventListener("canplay", onCanPlay);
      vid.removeEventListener("loadeddata", onCanPlay);
    };
  }, []);

  // Solo film (only one video in FILMS) fills the full section width with a
  // cinematic 21:9 crop on desktop, standard 16:9 on mobile — feels editorial
  // without letterboxing the drone footage.
  const aspect = solo
    ? "aspect-video md:aspect-[21/9]"
    : "aspect-video";
  return (
    <figure className="border border-soft_border overflow-hidden" data-testid={`film-${index}`}>
      <video
        ref={videoRef}
        src={film.src}
        poster={film.poster}
        controls
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        className={`w-full ${aspect} bg-black object-cover`}
      >
        Your browser does not support video playback.
      </video>
      <figcaption className="px-4 py-3 text-sm text-ionian/70 italic border-t border-soft_border bg-stone_white">
        {film.caption}
      </figcaption>
    </figure>
  );
}

export default function Home() {
  const [villa, setVilla] = useState(null);
  const { t } = useI18n();
  useEffect(() => { api.get("/villa").then(r => setVilla(r.data)).catch(() => {}); }, []);
  const minN = villa?.min_nights ?? 7;
  const items = t("amenities.items") || [];

  // Responsive map height — mobile phones get a shorter map so it doesn't dominate the viewport.
  const [mapHeight, setMapHeight] = useState(620);
  useEffect(() => {
    const pick = () => setMapHeight(window.innerWidth < 768 ? 380 : 620);
    pick();
    window.addEventListener("resize", pick);
    return () => window.removeEventListener("resize", pick);
  }, []);

  // Ordered list of every gallery image the visitor can click, in the exact
  // order they appear on screen. Used by the Lightbox for prev/next navigation.
  // Row 1 tiles come first (dining big + interior + view), then everything in
  // GALLERY_EXTRA in its declared order.
  const galleryImages = useMemo(() => [
    { src: IMG.dining,   alt: "Pergola dining at dusk, two warm lamps glowing" },
    { src: IMG.interior, alt: "The guest bedroom — cream headboard, framed abstract art, brass reading lamps" },
    { src: IMG.view,     alt: "Garden dining glimpsed through the leaves at dusk" },
    ...GALLERY_EXTRA,
  ], []);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  return (
    <>
      <Nav />

      {/* HERO — original evening-bistro still. Static image; the drone film
          moved to the "In Motion" section where it autoplays on scroll. */}
      <section className="relative h-[100vh] w-full overflow-hidden">
        <img
          src={IMG.hero}
          alt="Evening bistro table by the stone wall — Ithaca Garden Villa, Kioni"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="overline" style={{ color: "#E8DFD2" }}>{t("hero.tag")}</div>
            <h1 className="font-sans text-white text-4xl sm:text-5xl md:text-7xl font-extralight leading-[1.05] md:leading-[1.0] mt-6 max-w-4xl tracking-tight">
              {t("hero.headline_1")}<br/>{t("hero.headline_2")}
            </h1>
            <p className="text-white/85 mt-6 md:mt-8 max-w-xl text-base md:text-lg leading-relaxed font-light">{t("hero.sub")}</p>
            <div className="mt-8 md:mt-12 flex flex-wrap items-center gap-4 md:gap-5">
              <Link to="/booking" data-testid="hero-book-btn" className="btn-brass px-8 md:px-9 py-3.5 md:py-4 rounded-full text-xs md:text-sm tracking-widest uppercase">
                {t("hero.cta_book")}
              </Link>
              <a href="#story" data-testid="hero-learn-more" className="text-white/90 hover:text-white text-xs md:text-sm uppercase tracking-widest border-b border-white/40 pb-1">
                {t("hero.cta_learn")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 pt-24 pb-8 flex justify-center">
        <img src="/brand/logo-master-transparent.png" alt="Ithaca Garden Villa" className="w-full max-w-[780px] h-auto" data-testid="home-brand-logo" />
      </section>

      {/* URGENCY — pulled live from /availability/urgency; renders nothing if fully booked */}
      <UrgencyBanner />

      {/* STORY */}
      <section id="story" className="max-w-[1400px] mx-auto px-6 md:px-16 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline">{t("story.overline")}</div>
          <h2 className="font-serif text-4xl md:text-6xl font-light mt-6 leading-tight">{t("story.heading")}</h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-lg leading-[1.9] text-ionian/80 font-light">{t("story.body_1")}</p>
          <p className="mt-6 text-lg leading-[1.9] text-ionian/80 font-light">{t("story.body_2")}</p>
          <div className="mt-12 grid grid-cols-3 gap-6 hairline pt-8">
            <div><div className="overline">{t("story.bedrooms")}</div><div className="font-sans text-3xl mt-2 font-extralight tracking-tight">{villa?.bedrooms ?? 2}</div></div>
            <div><div className="overline">{t("story.guests")}</div><div className="font-sans text-3xl mt-2 font-extralight tracking-tight">{villa?.max_guests ?? 4}</div></div>
            <div><div className="overline">{t("story.min_stay")}</div><div className="font-sans text-3xl mt-2 font-extralight tracking-tight">{minN} {t("story.nights")}</div></div>
          </div>
          <p className="mt-8 text-sm text-ink/60 italic leading-relaxed" data-testid="bedding-note">
            {t("story.bedding")}
          </p>
        </div>
      </section>

      {/* THE VILLA — interior tour reel (Ken-Burns from ~24 professional photos) */}
      <section id="the-villa" className="bg-stone_white border-y border-soft_border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-10 md:mb-14">
            <div className="md:col-span-4">
              <div className="overline">{t("villa_reel.overline")}</div>
              <h2 className="font-sans text-4xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight">
                {t("villa_reel.heading")}
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 flex items-end">
              <p className="text-lg leading-[1.9] text-ionian/80 font-light">{t("villa_reel.body")}</p>
            </div>
          </div>
          <FilmTile
            film={{
              src: "/films/villa-tour.mp4",
              poster: "/films/villa-tour-poster.jpg",
              caption: t("villa_reel.caption"),
            }}
            index={99}
            solo
          />
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="max-w-[1400px] mx-auto px-6 md:px-16 pb-28">
        {/* Row 1 — editorial composition: one hero tile with a stacked pair */}
        <div className="grid grid-cols-12 gap-4 md:gap-6" style={{ gridAutoRows: "160px" }}>
          <button type="button" onClick={() => setLightboxIdx(0)} className="col-span-12 md:col-span-8 row-span-3 overflow-hidden p-0 border-0 bg-transparent cursor-zoom-in block" data-testid="gallery-tile-0" aria-label="Open photo"><img src={IMG.dining} alt="Pergola dining at dusk, two warm lamps glowing" className="w-full h-full object-cover zoom-hover" /></button>
          <button type="button" onClick={() => setLightboxIdx(1)} className="col-span-6 md:col-span-4 row-span-2 overflow-hidden p-0 border-0 bg-transparent cursor-zoom-in block" data-testid="gallery-tile-1" aria-label="Open photo"><img src={IMG.interior} alt="The guest bedroom — cream headboard, framed abstract art, brass reading lamps" className="w-full h-full object-cover zoom-hover" /></button>
          <button type="button" onClick={() => setLightboxIdx(2)} className="col-span-6 md:col-span-4 row-span-1 overflow-hidden p-0 border-0 bg-transparent cursor-zoom-in block" data-testid="gallery-tile-2" aria-label="Open photo"><img src={IMG.view} alt="Garden dining glimpsed through the leaves at dusk" className="w-full h-full object-cover zoom-hover" /></button>
        </div>

        {/* Rows 2, 3 and beyond — masonry-style grid on desktop, simpler aspect-
            ratio tiles on mobile so portraits/landscapes/banners never crop weirdly
            at 390-px viewport widths. On mobile: tiles size themselves via
            aspect-ratio (4:3 / 3:4 / 21:9). On md+: fixed 140-px grid rows +
            row-span for the masonry-with-dense-packing effect. */}
        <div
          className="grid grid-cols-6 gap-4 md:gap-6 mt-4 md:mt-6 md:[grid-auto-rows:140px] md:[grid-auto-flow:dense]"
          data-testid="gallery-extra"
        >
          {GALLERY_EXTRA.map((img, i) => {
            let cls;
            if (img.wide) cls = "col-span-6 aspect-[21/9] md:aspect-auto md:row-span-3";
            else if (img.tall) cls = "col-span-3 aspect-[3/4] md:aspect-auto md:col-span-2 md:row-span-4";
            else cls = "col-span-3 aspect-[4/3] md:aspect-auto md:col-span-2 md:row-span-2";
            // +3 because the first 3 entries in galleryImages are the row-1
            // editorial tiles (dining, interior, view) that come before GALLERY_EXTRA.
            const galleryIdx = i + 3;
            return (
              <button
                type="button"
                key={i}
                onClick={() => setLightboxIdx(galleryIdx)}
                className={`overflow-hidden p-0 border-0 bg-transparent cursor-zoom-in block ${cls}`}
                data-testid={`gallery-tile-${galleryIdx}`}
                aria-label="Open photo"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover zoom-hover"
                />
              </button>
            );
          })}
        </div>
      </section>

      <Lightbox
        images={galleryImages}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(-1)}
        onNav={setLightboxIdx}
      />

      {/* AMENITIES */}
      <section className="bg-white border-y border-soft_border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline">{t("amenities.overline")}</div>
            <h2 className="font-sans text-4xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight">{t("amenities.heading")}</h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-10">
            {items.map(([title, desc]) => (
              <div key={title} className="border-t border-soft_border pt-4">
                <div className="font-serif text-xl">{title}</div>
                <div className="text-sm text-ionian/60 mt-1 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section id="location" className="max-w-[1400px] mx-auto px-6 md:px-16 py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8 overflow-hidden">
          <Map height={mapHeight} className="border border-soft_border" showLabel={true} />
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <div className="overline">{t("location.overline")}</div>
          <h2 className="font-sans text-4xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight">{t("location.heading")}</h2>
          <p className="mt-6 text-lg leading-[1.9] text-ionian/80 font-light">{t("location.body")}</p>
          <div className="mt-8 grid grid-cols-2 gap-6 hairline pt-6">
            <div><div className="overline">{t("location.port")}</div><div className="font-serif text-lg md:text-2xl mt-1 whitespace-nowrap">{t("location.port_val")}</div></div>
            <div><div className="overline">{t("location.airport")}</div><div className="font-serif text-lg md:text-2xl mt-1 whitespace-nowrap">{t("location.airport_val")}</div></div>
          </div>
          <div className="mt-8 pt-6 hairline text-sm text-ionian/70 space-y-1" data-testid="location-address">
            <div className="overline mb-2">{t("location.find_us_overline")}</div>
            <div>{t("location.address_line")}</div>
            <div>{t("location.coords")}</div>
            <a
              href="mailto:bookings@ithacagardenvilla.com"
              data-testid="location-email"
              className="inline-block mt-1 text-teal hover:underline"
            >
              bookings@ithacagardenvilla.com
            </a>
          </div>
        </div>
      </section>

      {/* IN MOTION — films of the villa, village and island */}
      <section id="in-motion" className="bg-white border-t border-soft_border">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-10 md:mb-16">
            <div className="md:col-span-4">
              <div className="overline">{t("films.overline")}</div>
              <h2 className="font-sans text-4xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight">
                {t("films.heading")}
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 flex items-end">
              <p className="text-lg leading-[1.9] text-ionian/80 font-light">{t("films.body")}</p>
            </div>
          </div>

          <div
            className={`grid gap-6 md:gap-8 ${FILMS.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}
            data-testid="films-grid"
          >
            {FILMS.map((film, i) => (
              <FilmTile key={i} film={film} index={i} solo={FILMS.length === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO GET HERE */}
      <HowToGetHere />

      {/* CONCIERGE */}
      <ConciergeSection />

      {/* TESTIMONIAL */}
      <TestimonialSection />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="overline">{t("cta.overline")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl font-light mt-6 leading-[1.02] md:leading-[0.95]">{t("cta.heading")}</h2>
          <p className="mt-6 text-lg leading-[1.9] text-ionian/80 font-light max-w-xl">{t("cta.body", { n: minN })}</p>
          <Link to="/booking" data-testid="cta-book-btn" className="btn-ionian inline-block mt-10 px-10 py-4 rounded-full text-sm tracking-widest uppercase">
            {t("cta.button")}
          </Link>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <div className="border border-soft_border p-10 bg-white">
            <div className="overline">{t("cta.from")}</div>
            <div className="font-sans text-5xl mt-2 text-ink font-extralight tracking-tight">€{villa?.nightly_rate ?? 850}</div>
            <div className="text-sm text-ionian/60 mt-1">{t("cta.per_night")}</div>
            <div className="hairline mt-6 pt-6 text-sm text-ionian/70">
              {t("cta.cleaning", { n: villa?.cleaning_fee ?? 140 })}<br/>
              {t("cta.min", { n: minN })}<br/>
              {villa?.deposit_amount ? t("cta.deposit", { n: villa.deposit_amount }) : null}
            </div>
          </div>
        </div>
      </section>

      <ContactForm />
      <NewsletterBand />
      <Footer />
      <NewsletterPopup />
    </>
  );
}

function HowToGetHere() {
  const { t } = useI18n();
  const routes = t("location.howto.routes") || [];
  const ferries = t("location.howto.ferries") || [];
  return (
    <section id="how-to-get-here" className="border-t border-soft_border bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-24 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-5">
            <div className="overline">{t("location.howto.overline")}</div>
            <h2 className="font-sans text-3xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight">{t("location.howto.heading")}</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-ink/75 leading-relaxed">{t("location.howto.intro")}</p>
          </div>
        </div>

        {/* Routes */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {routes.map((r) => (
            <article key={r.badge} className="border border-soft_border p-8 bg-cream flex flex-col">
              <div className="flex items-baseline justify-between">
                <div className="font-sans text-4xl text-teal font-extralight tracking-tight">{r.badge}</div>
                <div className="overline">{r.duration}</div>
              </div>
              <h3 className="font-sans text-2xl mt-6 font-light tracking-tight">{r.title}</h3>
              <ul className="mt-6 space-y-3 text-sm text-ink/75 leading-relaxed flex-1">
                {r.steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-olive font-semibold min-w-[16px]">·</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {/* Ferries */}
        <div className="mt-16 max-w-2xl">
          <div className="overline">{t("location.howto.ferries_heading")}</div>
          <ul className="mt-6 space-y-4">
            {ferries.map(([name, routes]) => (
              <li key={name} className="border-t border-soft_border pt-4">
                <div className="font-sans text-lg tracking-tight">{name}</div>
                <div className="text-sm text-ink/60 mt-1">{routes}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 flex flex-col md:flex-row justify-between gap-4 text-xs text-ink/55 italic">
          <div className="max-w-2xl leading-relaxed">{t("location.howto.note")}</div>
          <a
            href="https://www.ithacagreece.com/How%20to%20get%20there/howtoget.html"
            target="_blank" rel="noreferrer noopener"
            className="hover:text-teal not-italic tracking-tight underline underline-offset-4 decoration-soft_border"
          >
            {t("location.howto.source")}
          </a>
        </div>
      </div>
    </section>
  );
}

function ConciergeSection() {
  const { t } = useI18n();
  const services = t("concierge.services") || [];
  return (
    <section id="concierge" className="border-t border-soft_border bg-cream">
      <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-24 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-5">
            <div className="overline">{t("concierge.overline")}</div>
            <h2 className="font-sans text-3xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight" data-testid="concierge-heading">
              {t("concierge.heading")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-ink/75 leading-relaxed">{t("concierge.body")}</p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-testid="concierge-services">
          {services.map(([title, desc]) => (
            <div key={title} className="border-t border-soft_border pt-6">
              <div className="font-serif text-2xl leading-tight">{title}</div>
              <div className="text-sm text-ionian/65 mt-3 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-xs text-ink/55 italic max-w-2xl leading-relaxed">
          {t("concierge.contact_line")}
        </div>

        <a
          href="https://instagram.com/ithacagardenvilla"
          target="_blank"
          rel="noreferrer noopener"
          data-testid="concierge-instagram-link"
          className="group inline-flex items-center gap-4 mt-10 pt-6 border-t border-soft_border text-ionian hover:text-brass transition"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor"/>
          </svg>
          <span className="font-serif text-base tracking-wide">
            {t("concierge.instagram_line")}
            <span className="ml-2 text-brass tracking-widest text-xs uppercase group-hover:tracking-[0.24em] transition-[letter-spacing]">
              @ithacagardenvilla
            </span>
          </span>
        </a>

        <InstagramGallery />
      </div>
    </section>
  );
}

function InstagramGallery() {
  const { t } = useI18n();
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let ignore = false;
    api.get("/instagram/posts")
      .then(r => { if (!ignore) setPosts(r.data || []); })
      .catch(() => { if (!ignore) setPosts([]); });
    return () => { ignore = true; };
  }, []);
  // Silently hide the section until credentials are configured and posts arrive
  if (!posts || posts.length === 0) return null;
  const three = posts.slice(0, 3);
  return (
    <div className="mt-14 pt-10 border-t border-soft_border" data-testid="concierge-instagram-feed">
      <div className="overline text-brass mb-6">{t("concierge.instagram_recent")}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {three.map(p => (
          <a
            key={p.id}
            href={p.permalink}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`ig-post-${p.id}`}
            className="group block overflow-hidden bg-ivory relative aspect-square"
          >
            <img
              src={p.image_url}
              alt={(p.caption || "Ithaca Garden Villa on Instagram").slice(0, 200)}
              loading="lazy"
              className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500">
              <div className="text-[11px] tracking-widest uppercase text-white/90">View on Instagram</div>
              {p.caption && (
                <div className="text-xs text-white/80 mt-1 line-clamp-2">{p.caption.slice(0, 90)}</div>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}



function TestimonialSection() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  // Rotate every 8 seconds; if we have no published reviews, fall back to the hardcoded one.
  useEffect(() => {
    if (items.length < 2) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % items.length), 8000);
    return () => clearInterval(timer);
  }, [items.length]);

  const current = items[idx];
  return (
    <section className="bg-ionian text-stone_white" data-testid="testimonial-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-16 py-28 text-center">
        {current ? (
          <>
            <div className="overline" style={{ color: "#E8DFD2" }}>{current.period || t("testimonial.tag")}</div>
            <blockquote data-testid="testimonial-quote" className="font-sans text-2xl md:text-4xl leading-[1.35] font-extralight mt-8 tracking-tight max-w-3xl mx-auto">
              “{current.quote}”
            </blockquote>
            <div className="mt-10 overline" style={{ color: "#E8DFD2" }}>— {current.display_name}</div>
            {items.length > 1 && (
              <div className="mt-8 flex justify-center gap-2" data-testid="testimonial-dots">
                {items.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    aria-label={`Testimonial ${i + 1}`}
                    className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? "bg-stone_white" : "bg-stone_white/30 hover:bg-stone_white/60"}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="overline" style={{ color: "#E8DFD2" }}>{t("testimonial.tag")}</div>
            <blockquote className="font-sans text-2xl md:text-4xl leading-[1.35] font-extralight mt-8 tracking-tight max-w-3xl mx-auto">{t("testimonial.quote")}</blockquote>
            <div className="mt-10 overline" style={{ color: "#E8DFD2" }}>{t("testimonial.author")}</div>
          </>
        )}
      </div>
    </section>
  );
}


function NewsletterBand() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const val = email.trim().toLowerCase();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setErr(t("newsletter.error"));
      setState("error");
      return;
    }
    setState("submitting");
    try {
      await api.post("/newsletter/subscribe", { email: val, name: name.trim(), language: lang || "en", source: "home_footer" });
      try { window.localStorage.setItem("ithaca_nl_seen", String(Date.now())); } catch {}
      setState("done");
      setEmail(""); setName("");
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("newsletter.error"));
      setState("error");
    }
  }

  return (
    <section id="newsletter" className="border-t border-soft_border bg-stone_white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-24 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="overline">{t("newsletter.overline")}</div>
            <h2 className="font-sans text-3xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight" data-testid="newsletter-heading">
              {t("newsletter.heading")}
            </h2>
            <p className="text-ink/70 mt-6 leading-relaxed max-w-md">{t("newsletter.body")}</p>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            {state === "done" ? (
              <div data-testid="newsletter-success" className="border border-brass/40 bg-cream p-8">
                <div className="overline text-brass">✓</div>
                <div className="font-serif text-2xl mt-2">{t("newsletter.success")}</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="newsletter-form">
                <div>
                  <label className="overline block mb-2" htmlFor="nl-name">Name</label>
                  <input
                    id="nl-name"
                    data-testid="newsletter-name"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={t("newsletter.name_placeholder")}
                    className="editorial-input w-full"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="nl-email">Email</label>
                  <input
                    id="nl-email"
                    data-testid="newsletter-email"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t("newsletter.email_placeholder")}
                    className="editorial-input w-full"
                    autoComplete="email"
                  />
                </div>
                {err && <div data-testid="newsletter-error" className="text-terracotta text-sm">{err}</div>}
                <button
                  type="submit"
                  disabled={state === "submitting"}
                  data-testid="newsletter-submit"
                  className="btn-ionian px-8 py-3 rounded-full text-sm tracking-widest uppercase self-start disabled:opacity-50"
                >
                  {state === "submitting" ? "…" : t("newsletter.submit")}
                </button>
                <div className="text-xs text-ink/50 italic max-w-lg leading-relaxed pt-2">
                  {t("newsletter.consent")}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const { t } = useI18n();

  async function submit(e) {
    e.preventDefault();
    setSending(true); setErr("");
    try {
      await api.post("/inquiries", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      setErr(t("contact.error"));
    } finally { setSending(false); }
  }

  return (
    <section id="contact" className="bg-white border-y border-soft_border">
      <div className="max-w-[1100px] mx-auto px-6 md:px-16 py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline">{t("contact.overline")}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-6 leading-tight">{t("contact.heading")}</h2>
          <p className="mt-6 text-ionian/70 leading-relaxed">{t("contact.body")}</p>
        </div>
        <form onSubmit={submit} className="md:col-span-6 md:col-start-7 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="md:col-span-2">
            {sent && <div data-testid="inquiry-success" className="text-olive text-sm mb-4">{t("contact.success")}</div>}
            {err && <div data-testid="inquiry-error" className="text-terracotta text-sm mb-4">{err}</div>}
          </div>
          <div>
            <div className="overline mb-1">{t("contact.name")}</div>
            <input required data-testid="inquiry-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="editorial-input w-full" />
          </div>
          <div>
            <div className="overline mb-1">{t("contact.email")}</div>
            <input required type="email" data-testid="inquiry-email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="editorial-input w-full" />
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-1">{t("contact.phone")}</div>
            <input data-testid="inquiry-phone" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="editorial-input w-full" />
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-1">{t("contact.message")}</div>
            <textarea required rows={4} data-testid="inquiry-message" value={form.message} onChange={e=>setForm({...form,message:e.target.value})} className="editorial-input w-full resize-none" />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={sending} data-testid="inquiry-submit" className="btn-ionian px-8 py-3 rounded-full text-sm uppercase tracking-widest disabled:opacity-50">
              {sending ? t("contact.sending") : t("contact.submit")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
